import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { FetchArgs } from '@reduxjs/toolkit/query';

/** The slice of state the base query needs; kept structural to avoid importing the store. */
interface TokenState {
  ownedPastes: { byId: Record<string, { deleteToken: string } | undefined> };
}

const pastePath = /^\/api\/paste\/([0-9A-Za-z]{11})$/;

/**
 * Attaches the delete token for DELETE requests. The contract declares it as a bearer security
 * scheme, which the generated endpoints do not take as an argument; the token lives in the store,
 * keyed by paste id, so it is looked up here instead.
 */
export function authorizationFor(state: TokenState, args: string | FetchArgs): string | undefined {
  if (typeof args === 'string' || args.method !== 'DELETE') {
    return undefined;
  }
  const id = pastePath.exec(args.url)?.[1];
  const token = id === undefined ? undefined : state.ownedPastes.byId[id]?.deleteToken;
  return token === undefined ? undefined : `Bearer ${token}`;
}

/** Empty API that the generated endpoints (./generated.ts) are injected into. */
export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    // same-origin API; absolute because fetch outside a browser (tests) rejects relative URLs
    baseUrl: `${globalThis.location.origin}/`,
    prepareHeaders: (headers, { getState, arg }) => {
      const authorization = authorizationFor(getState() as TokenState, arg);
      if (authorization !== undefined) {
        headers.set('Authorization', authorization);
      }
      return headers;
    },
  }),
  endpoints: () => ({}),
});
